import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ULThemeSocialProviderButtonProps
  extends React.HTMLAttributes<HTMLButtonElement>,
    ButtonProps {
  /**
   * The icon to be displayed in the social login button component
   */
  iconComponent: React.ReactNode | null;
  /**
   * The display Name to be displayed in the social login button component
   */
  displayName: string;
  /**
   * The button text to be displayed in the social login button component
   */
  buttonText: string;
  /**
   * Optional class names for additional styling or overriding default styles.
   */
  className?: string;
}

type SocialProvider = "apple" | "default";

const ULThemeSocialProviderButton = ({
  onClick,
  variant = "outline",
  size = "default",
  iconComponent,
  displayName,
  buttonText,
  disabled = false,
  className,
  ...rest
}: ULThemeSocialProviderButtonProps) => {
  const dataTestId = `social-provider-button-${displayName.toLowerCase().replace(/\s+/g, "-")}`;

  const baseStyles =
    "flex items-center justify-start w-full h-[52px] py-[14px] px-[16px] gap-x-4";

  const enabledStyles =
    "bg-white border-gray-mid text-text-default cursor-pointer";
  const disabledStyles =
    "bg-gray-mid/10 border-gray-mid/50 text-text-secondary cursor-not-allowed";

  const detectProvider = (name: string): SocialProvider => {
    const normalizedName = name.toLowerCase().trim();
    if (normalizedName === "apple") return "apple";
    return "default";
  };

  const providerVariants: Record<SocialProvider, string> = {
    apple:
      "!bg-black !text-white !border-black hover:!bg-gray-900 focus:ring-gray-800/30",
    default: "",
  };

  const currentProvider = detectProvider(displayName);
  const applyProviderStyles = providerVariants[currentProvider];
  const hasProviderOverride = currentProvider !== "default";

  // UL theme overrrides
  const variantThemeOverrides = {
    outline: cn(
      "theme-universal:font-button", //font-weight
      "theme-universal:rounded-button", // border-radius
      "theme-universal:border-(length:--ul-theme-border-button-border-weight)", // border-weight or border-width
      !hasProviderOverride &&
        "theme-universal:border-(--color-secondary-button-border)", // border-color
      !hasProviderOverride &&
        "theme-universal:text-(--color-secondary-button-label)", // text label color
      "theme-universal:hover:shadow-[var(--button-hover-shadow)]", // box-shadow on hover
      "theme-universal:focus:outline-none theme-universal:focus:ring-4 theme-universal:focus:ring-base-focus/15 theme-universal:focus:bg-base-focus/15" // focus base color
    ),
    primary: "", // Add primary overrides if needed
    secondary: "", // Add secondary overrides if needed
    destructive: "", // Add destructive overrides if needed
    ghost: "", // Add ghost overrides if needed
    link: "", // Add link overrides if needed
  };

  // Combine all theme classes with proper type safety
  const themeClasses = cn(variant && variantThemeOverrides[variant]);

  return (
    <Button
      variant={variant}
      size={size}
      className={cn(
        baseStyles,
        disabled ? disabledStyles : enabledStyles,
        themeClasses,
        applyProviderStyles,
        className
      )}
      data-testid={dataTestId}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {iconComponent && (
        <span className="w-5 h-5 flex items-center justify-center">
          {iconComponent}
        </span>
      )}
      <span className="ul-social-provider-button-text overflow-hidden whitespace-nowrap text-ellipsis text-base">
        {buttonText}
      </span>
    </Button>
  );
};

export default ULThemeSocialProviderButton;
